import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell } from "@phosphor-icons/react";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list.data);
      setUnread(count.data.count);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const openPanel = async () => {
    setOpen((v) => !v);
    if (!open) await load();
  };

  const goTo = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.lead_id) navigate(`/leads/${n.lead_id}`);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((arr) => arr.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={openPanel}
        data-testid="notification-bell"
        className="relative p-2 hover:bg-secondary border border-transparent hover:border-border transition-colors"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-destructive text-white text-[10px] font-bold flex items-center justify-center tabular-nums"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 w-80 bg-white border border-border shadow-sm z-50 fade-up"
          data-testid="notification-panel"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-display text-sm font-bold tracking-tight">Notifications</h3>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="mark-all-read"
                className="text-[10px] uppercase tracking-[0.15em] font-bold text-primary hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                No notifications yet.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      onClick={() => goTo(n)}
                      data-testid={`notification-item-${n.id}`}
                      className={`w-full text-left px-4 py-3 hover:bg-secondary/60 transition-colors flex gap-3 items-start ${
                        n.read ? "" : "bg-primary/5"
                      }`}
                    >
                      <span
                        className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                          n.read ? "bg-muted-foreground/40" : "bg-primary"
                        }`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-semibold truncate">
                          {n.title}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          {n.body}
                        </span>
                        <span className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1.5 tabular-nums">
                          {new Date(n.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
